/* eslint-disable camelcase */
import React, { useContext, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import queryString from 'query-string';
import { Typography, Grid, Alert, Button } from '@mui/material';
import { ConnectorContext } from '../../context';

const REDIRECT = 'REDIRECT';

// Onboarding steps that have an in-app previous step to return to. On the
// OAuth callback (step-authorized) or the application selector, history.back()
// would leave the onboarding flow, so those keep the Cancel button.
const STEP_WITH_PREVIOUS_STEP = /\/connect\/(step\/[^/]+|last-step)\/?$/;

export const ErrorPage = ({ errorMsgId, padding }) => {
  const {
    state: { returnUri, errorMessage, errorAction, isValidState },
    updateState,
  } = useContext(ConnectorContext);

  const { search, pathname } = useLocation();
  const navigate = useNavigate();
  const params = queryString.parse(search);
  const { error, error_description } = params;

  const canGoBack = Boolean(isValidState) && STEP_WITH_PREVIOUS_STEP.test(pathname);

  const goBack = () => {
    updateState({ error: false, errorMessage: undefined, errorAction: undefined });
    navigate(-1);
  };

  useEffect(() => {
    if (errorAction === REDIRECT) {
      setTimeout(() => {
        window.location.href = returnUri;
      }, 3000);
    }
  }, [errorAction, returnUri]);

  const renderAction = () => {
    if (errorAction === REDIRECT) return null;

    if (canGoBack) {
      return (
        <Button
          variant="contained"
          onClick={goBack}
          sx={{ marginTop: '30px' }}
          fullWidth
          data-testid="error_back_btn"
        >
          <FormattedMessage id="back" />
        </Button>
      );
    }

    if (!returnUri) return null;

    return (
      <Button variant="contained" href={returnUri} sx={{ marginTop: '30px' }} fullWidth>
        <FormattedMessage id="cancel_btn" />
      </Button>
    );
  };

  return (
    <Grid container item justifyContent="center" sx={{ padding: padding || '60px 0' }}>
      {error ? (
        <Typography variant="body1" mb={2}>
          <FormattedMessage id={`Error: ${error}` || 'general_error_msg'} />
        </Typography>
      ) : null}
      <Alert severity="error">
        {!error || !error_description ? (
          <FormattedMessage id={errorMessage || errorMsgId || 'general_error_msg'} />
        ) : null}
        {error_description ? (
          <FormattedMessage
            id={`User does not have permission to onboard. Description: ${error_description}`}
          />
        ) : null}
      </Alert>
      {renderAction()}
    </Grid>
  );
};

ErrorPage.propTypes = {
  errorMsgId: PropTypes.string,
  padding: PropTypes.string,
};

ErrorPage.defaultProps = {
  errorMsgId: '',
  padding: '',
};
